import { ArrowLeftIcon, Button, CloseIcon, EmptyState, InfoIcon, Modal } from '@bp/ui-components';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import {
  CurriculumVersionsListQuery,
  useCurriculumVersionsListQuery,
} from '../../../types/planung-graphql-client-defs';
import { TransferCurriculaForm } from '../Forms/TransferCurriculaForm';
import styles from './TransferCurriculaModal.module.scss';

export type TransferCurriculaVersion = CurriculumVersionsListQuery['curriculumVersions'][number];

type TransferCurriculaModalProps = {
  isOpen: boolean;
  onClose: () => void;
};

export const TransferCurriculaModal = ({ isOpen, onClose }: TransferCurriculaModalProps) => {
  const { t } = useTranslation();
  const context = useMemorizedCacheTag('CURRICULUM_VERSIONS');

  const [loading, setLoading] = useState(false);
  const [selectedVersion, setSelectedVersion] = useState<TransferCurriculaVersion | null>(null);

  const [{ data }] = useCurriculumVersionsListQuery({
    context,
    pause: !isOpen,
  });

  const versions = data?.curriculumVersions ?? [];

  function handleClose() {
    setSelectedVersion(null);
    onClose();
  }

  return (
    <Modal
      className={styles['transfer-curricula-modal']}
      contentClasses={styles.content}
      hasScrollbar={false}
      customHeader={
        <div className={styles.header}>
          {selectedVersion && (
            <Button
              className={styles.button}
              hierarchy='tertiary'
              icon={<ArrowLeftIcon />}
              onClick={() => setSelectedVersion(null)}
            />
          )}
          <div className={styles.title}>
            {selectedVersion ? selectedVersion.name : t('curriculum.transferCurriculum')}
          </div>
          <Button
            className={selectedVersion ? undefined : styles.button}
            hierarchy='tertiary'
            icon={<CloseIcon />}
            onClick={handleClose}
          />
        </div>
      }
      isOpen={isOpen}
      onRequestClose={handleClose}
      loading={loading}
      width='l'
    >
      {selectedVersion ? (
        <TransferCurriculaForm
          key={selectedVersion.uuid}
          version={selectedVersion}
          onLoad={setLoading}
          onClose={handleClose}
        />
      ) : versions.length === 0 ? (
        <EmptyState title={t('curriculumVersion.empty')} icon={<InfoIcon />} />
      ) : (
        <div className={styles['version-list']}>
          {versions.map((version) => (
            <div key={version.uuid} className={styles.version} onClick={() => setSelectedVersion(version)}>
              <div className={styles.name}>{version.name}</div>
              {version.description && <div className={styles.description}>{version.description}</div>}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};
